import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useLocation, useMatches } from 'react-router-dom';

import { useFeatureFlag } from '@/app/services/features';
import { useCurrentUser } from '@/services/auth';
import { useWebSocket } from '@/utils/web-socket';

import { CursorInfo, CursorOverlay, FADE_AFTER_MS } from './CursorOverlay';

type PresenceHandle = {
  presence?: boolean;
};

const SEND_INTERVAL_MS = 40;
const SMOOTHING = 0.3;

const CURSOR_COLORS = [
  '#e5484d',
  '#f76b15',
  '#ffc53d',
  '#46a758',
  '#12a594',
  '#0090ff',
  '#3e63dd',
  '#8e4ec6',
  '#d6409f',
  '#ad7f58',
];

type CursorPositionMessage = {
  type: 'cursor_position';
  id: string;
  name: string;
  x: number;
  y: number;
};

type CursorPositionUpdate = {
  type: 'cursor_position';
  room: string;
  x: number;
  y: number;
};

type Cursor = CursorInfo & {
  targetX: number;
  targetY: number;
  x: number;
  y: number;
  lastSeen: number;
};

// FNV-1a, so every viewer derives the same color for the same connection.
function colorFor(connectionId: string) {
  let hash = 2166136261;

  for (let i = 0; i < connectionId.length; i++) {
    hash = Math.imul(hash ^ connectionId.charCodeAt(i), 16777619) >>> 0;
  }

  return CURSOR_COLORS[hash % CURSOR_COLORS.length];
}

// Includes the part of the content scrolled out of view.
function contentSize(el: HTMLElement) {
  return {
    width: Math.max(el.clientWidth, el.scrollWidth),
    height: Math.max(el.clientHeight, el.scrollHeight),
  };
}

export function PresenceCursors({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const enabled = useFeatureFlag('presence_cursors');
  const { pathname, search } = useLocation();
  const room = pathname + search;
  const matches = useMatches();

  const routeOptedIn = matches.some(
    (m) => (m.handle as PresenceHandle | undefined)?.presence,
  );
  const active = routeOptedIn && enabled;
  const { data: currentUser } = useCurrentUser();
  const activeCompanyCode = currentUser?.companies.find(
    (c) => c.isActive,
  )?.code;

  const containerRef = useRef<HTMLDivElement>(null);
  const cursorsRef = useRef(new Map<string, Cursor>());
  const elementsRef = useRef(new Map<string, HTMLDivElement>());
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const lastSentRef = useRef<{ x: number; y: number; at: number } | null>(null);
  const roomRef = useRef(room);
  const activeRef = useRef(active);

  const [visible, setVisible] = useState<CursorInfo[]>([]);

  useEffect(() => {
    roomRef.current = room;
    activeRef.current = active;
  }, [room, active]);

  const syncVisible = useCallback(() => {
    setVisible(
      [...cursorsRef.current.values()].map(({ id, name, color }) => ({
        id,
        name,
        color,
      })),
    );
  }, []);

  const clearCursors = useCallback(() => {
    if (cursorsRef.current.size === 0) {
      return;
    }

    cursorsRef.current.clear();
    syncVisible();
  }, [syncVisible]);

  const registerElement = useCallback(
    (id: string, node: HTMLDivElement | null) => {
      if (node) {
        elementsRef.current.set(id, node);
      } else {
        elementsRef.current.delete(id);
      }
    },
    [],
  );

  useLayoutEffect(() => {
    clearCursors();
    lastSentRef.current = null;
  }, [room, clearCursors]);

  const handleCursorPosition = useCallback(
    (msg: CursorPositionMessage) => {
      if (msg.type !== 'cursor_position' || !activeRef.current) {
        return;
      }

      const now = performance.now();
      const existing = cursorsRef.current.get(msg.id);

      if (existing) {
        existing.targetX = msg.x;
        existing.targetY = msg.y;
        existing.lastSeen = now;

        const node = elementsRef.current.get(msg.id);

        // Restart the fade: drop the class, force a reflow, add it back.
        if (node) {
          node.classList.remove('animate-cursor-fade');
          void node.offsetWidth;
          node.classList.add('animate-cursor-fade');
        }

        return;
      }

      cursorsRef.current.set(msg.id, {
        id: msg.id,
        name: msg.name,
        color: colorFor(msg.id),
        targetX: msg.x,
        targetY: msg.y,
        x: msg.x,
        y: msg.y,
        lastSeen: now,
      });
      syncVisible();
    },
    [syncVisible],
  );

  const { connect, disconnect, send } = useWebSocket<
    CursorPositionUpdate,
    CursorPositionMessage
  >({
    path: '/ws/presence',
    onMessage: handleCursorPosition,
    onClose: clearCursors,
  });

  useEffect(() => {
    if (!active || !activeCompanyCode) {
      return;
    }

    connect();

    return () => {
      disconnect();
      clearCursors();
    };
  }, [active, activeCompanyCode, connect, disconnect, clearCursors]);

  useEffect(() => {
    const el = containerRef.current;

    if (!active || !el) {
      return;
    }

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const { width, height } = contentSize(el);

      if (!width || !height) {
        return;
      }

      pointerRef.current = {
        x: (e.clientX - rect.left) / width,
        y: (e.clientY - rect.top) / height,
      };
    };

    const onLeave = () => {
      pointerRef.current = null;
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);

    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      pointerRef.current = null;
    };
  }, [active]);

  useEffect(() => {
    if (!active) {
      return;
    }

    let frame = 0;

    const sendPosition = (now: number) => {
      const pos = pointerRef.current;
      const last = lastSentRef.current;

      if (!pos || document.hidden) {
        return;
      }

      if (last && now - last.at < SEND_INTERVAL_MS) {
        return;
      }

      const changed = !last || last.x !== pos.x || last.y !== pos.y;

      if (!changed) {
        return;
      }

      const sent = send({
        type: 'cursor_position',
        room: roomRef.current,
        x: pos.x,
        y: pos.y,
      });

      if (sent) {
        lastSentRef.current = { ...pos, at: now };
      }
    };

    const moveCursors = (now: number) => {
      const el = containerRef.current;

      if (!el) {
        return;
      }

      const { width, height } = contentSize(el);
      let removed = false;

      for (const cursor of cursorsRef.current.values()) {
        const age = now - cursor.lastSeen;

        if (age > FADE_AFTER_MS) {
          cursorsRef.current.delete(cursor.id);
          removed = true;
          continue;
        }

        cursor.x += (cursor.targetX - cursor.x) * SMOOTHING;
        cursor.y += (cursor.targetY - cursor.y) * SMOOTHING;

        const node = elementsRef.current.get(cursor.id);

        if (node) {
          node.style.transform = `translate(${cursor.x * width}px, ${cursor.y * height}px)`;
        }
      }

      if (removed) {
        syncVisible();
      }
    };

    const tick = () => {
      frame = requestAnimationFrame(tick);

      const now = performance.now();

      sendPosition(now);
      moveCursors(now);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [active, send, syncVisible]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative' }}
    >
      {children}

      {active && visible.length > 0 && (
        <CursorOverlay cursors={visible} registerElement={registerElement} />
      )}
    </div>
  );
}
